import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Scan, Printer, Wifi, WifiOff, Loader2, Save, Check, X, 
  TestTube, Settings, AlertTriangle, RefreshCw, CreditCard, Monitor,
  Trash2, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { connectQZ, listPrinters, setPrinter, loadPrinterConfig, getBrowserPrintMode, setBrowserPrintMode, getQzPreferred, setQzPreferred } from '../services/PrinterService';

const HardwareSettings = () => {
  const { token } = useAuth();
  const { stores, selectedStore, getCurrentStore, storeName } = useStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingPrinter, setTestingPrinter] = useState(false);
  
  // Scanner settings
  const [scannerSettings, setScannerSettings] = useState({
    enabled: true,
    suffix_key: 'Enter',
    beep_on_scan: true,
    auto_add_to_cart: true,
    focus_search_on_scan: true
  });
  
  // Printer settings per store
  const [printerSettings, setPrinterSettings] = useState({
    orewa: {
      enabled: false,
      type: 'star_tsp100',
      ip_address: '',
      port: '80',
      paper_width: '80mm',
      auto_print_receipt: true,
      print_copies: 1
    },
    eastridge: {
      enabled: false,
      type: 'star_tsp100',
      ip_address: '',
      port: '80',
      paper_width: '80mm',
      auto_print_receipt: true,
      print_copies: 1
    }
  });
  
  // Label printer settings — per-store (Zebra GC420d). Mirrors `printerSettings`
  // structure for receipt printers so each store can have its own physical
  // device, IP, label size, etc.
  const defaultLabelSettings = React.useMemo(() => ({
    enabled: false,
    type: 'zebra_gc420',
    connection: 'browser_print',
    ip_address: '',
    port: '9100',
    label_width: '30',
    label_height: '22',
    columns: '3',
    print_density: '8',
    print_speed: '2',
    include_barcode: true,
    include_price: true,
    include_store_name: false,
    barcode_type: 'code128'
  }), []);
  const [labelPrinterSettings, setLabelPrinterSettings] = useState({
    orewa: { ...defaultLabelSettings },
    eastridge: { ...defaultLabelSettings, enabled: true },
  });
  const [selectedLabelStore, setSelectedLabelStore] = useState('eastridge');

  // Convenience: current store's label settings + setter
  const currentLabelSettings = labelPrinterSettings[selectedLabelStore] || defaultLabelSettings;
  const updateCurrentLabelSettings = (patch) => {
    setLabelPrinterSettings(prev => ({
      ...prev,
      [selectedLabelStore]: { ...prev[selectedLabelStore], ...patch },
    }));
  };

  // QZ Tray state
  const [qzConnected, setQzConnected] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [printerAssignments, setPrinterAssignments] = useState({ receipt: '', label: '', document: '' });
  const [detectingPrinters, setDetectingPrinters] = useState(false);

  // Load saved printer config on mount
  useEffect(() => {
    const saved = loadPrinterConfig();
    setPrinterAssignments({ 
      receipt: saved.receipt || '', 
      label: saved.label || '', 
      document: saved.document || '' 
    });
  }, []);

  const detectPrinters = async () => {
    setDetectingPrinters(true);
    try {
      const ok = await connectQZ();
      setQzConnected(ok);
      if (ok) {
        const printers = await listPrinters();
        setAvailablePrinters(printers);
        toast.success(`Found ${printers.length} printer(s)`);
      } else {
        toast.error('QZ Tray not running. Please make sure it is installed and running.');
      }
    } catch {
      toast.error('Failed to connect to QZ Tray');
    } finally {
      setDetectingPrinters(false);
    }
  };

  const savePrinterAssignment = (jobType, printerName) => {
    setPrinter(jobType, printerName);
    setPrinterAssignments(prev => ({ ...prev, [jobType]: printerName }));
    toast.success(`${jobType.charAt(0).toUpperCase() + jobType.slice(1)} printer set to: ${printerName}`);
  };
  
  const [labelPrinterStatus, setLabelPrinterStatus] = useState({
    connected: false,
    status: 'unknown'
  });
  
  const [printerStatus, setPrinterStatus] = useState({
    orewa: { connected: false, status: 'unknown' },
    eastridge: { connected: false, status: 'unknown' }
  });
  
  // Store details state
  const [storeDetails, setStoreDetails] = useState({
    orewa: { address: '', phone: '', website: '', gst_number: '' },
    eastridge: { address: '', phone: '', website: '', gst_number: '' }
  });
  const [savingStore, setSavingStore] = useState(null);
  
  // Smartpay EFTPOS settings per store (legacy single-terminal compatibility)
  const [smartpaySettings, setSmartpaySettings] = useState({
    orewa: { enabled: false, ip_address: '', port: '80' },
    eastridge: { enabled: false, ip_address: '', port: '80' }
  });
  const [smartpayStatus, setSmartpayStatus] = useState({
    orewa: { connected: false, status: 'unknown', paired: false },
    eastridge: { connected: false, status: 'unknown', paired: false }
  });
  // Multi-terminal state — each store has an array of terminals.
  // { id, name, register_id, environment, pairing_code, paired }
  const [storeTerminals, setStoreTerminals] = useState({ orewa: [], eastridge: [] });
  const [testingSmartpay, setTestingSmartpay] = useState(null);
  const [pairingSmartpay, setPairingSmartpay] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  
  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);
  
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/hardware`, { headers: getAuthHeader() });
      if (response.data.scanner) {
        setScannerSettings(response.data.scanner);
      }
      if (response.data.printers) {
        setPrinterSettings(response.data.printers);
      }
      if (response.data.label_printer) {
        const lp = response.data.label_printer;
        // Backward compat: if a legacy single-object config is stored (no .orewa
        // or .eastridge keys), treat it as Eastridge's existing config and leave
        // Orewa at defaults — preserves the user's current Eastridge setup.
        if (lp && (lp.orewa || lp.eastridge)) {
          setLabelPrinterSettings({
            orewa: { ...defaultLabelSettings, ...(lp.orewa || {}) },
            eastridge: { ...defaultLabelSettings, ...(lp.eastridge || {}) },
          });
        } else if (lp && typeof lp === 'object') {
          setLabelPrinterSettings({
            orewa: { ...defaultLabelSettings },
            eastridge: { ...defaultLabelSettings, ...lp },
          });
        }
      }
      if (response.data.smartpay) {
        setSmartpaySettings(response.data.smartpay);
      }
    } catch (error) {
      // Use defaults if no settings saved
      console.log('Using default hardware settings');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeader, defaultLabelSettings]);
  
  // Fetch store details from stores
  useEffect(() => {
    if (stores && stores.length > 0) {
      const details = {};
      stores.forEach(store => {
        details[store.code] = {
          address: store.address || '',
          phone: store.phone || '',
          website: store.website || '',
          gst_number: store.gst_number || ''
        };
      });
      setStoreDetails(details);
    }
  }, [stores]);
  
  const updateStoreDetail = (storeCode, field, value) => {
    setStoreDetails(prev => ({
      ...prev,
      [storeCode]: {
        ...prev[storeCode],
        [field]: value
      }
    }));
  };
  
  const saveStoreDetails = async (storeCode) => {
    setSavingStore(storeCode);
    try {
      await axios.put(`${API_URL}/stores/${storeCode}`, storeDetails[storeCode], { headers: getAuthHeader() });
      toast.success(`${storeCode.charAt(0).toUpperCase() + storeCode.slice(1)} store details saved`);
    } catch (error) {
      toast.error('Failed to save store details');
    } finally {
      setSavingStore(null);
    }
  };
  
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Load each store's saved terminals[] array whenever stores list arrives
  useEffect(() => {
    if (!stores || stores.length === 0) return;
    const next = { orewa: [], eastridge: [] };
    stores.forEach(s => {
      if (s.code && next[s.code] !== undefined) {
        next[s.code] = Array.isArray(s.terminals) ? s.terminals.map(t => ({
          id: t.id || `${s.code}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: t.name || 'Main Register',
          register_id: t.register_id || '',
          environment: t.environment || 'prod',
          pairing_code: t.pairing_code || '',
          paired: !!t.paired,
        })) : [];
      }
    });
    setStoreTerminals(next);
  }, [stores]);

  // ===== Multi-terminal helpers =====
  const addTerminal = (storeCode) => {
    setStoreTerminals(prev => ({
      ...prev,
      [storeCode]: [...(prev[storeCode] || []), {
        id: `${storeCode}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: `Register ${(prev[storeCode]?.length || 0) + 1}`,
        register_id: `${storeCode}-register-${(prev[storeCode]?.length || 0) + 1}`,
        environment: 'prod',
        pairing_code: '',
        paired: false,
      }],
    }));
  };

  const removeTerminal = (storeCode, terminalId) => {
    if (!window.confirm('Remove this terminal? Pairing will be lost.')) return;
    setStoreTerminals(prev => ({
      ...prev,
      [storeCode]: (prev[storeCode] || []).filter(t => t.id !== terminalId),
    }));
    persistTerminals(storeCode, (storeTerminals[storeCode] || []).filter(t => t.id !== terminalId));
  };

  const updateTerminal = (storeCode, terminalId, patch) => {
    setStoreTerminals(prev => ({
      ...prev,
      [storeCode]: (prev[storeCode] || []).map(t => t.id === terminalId ? { ...t, ...patch } : t),
    }));
  };

  // "Replace physical terminal" — used when staff swap in a new EFTPOS terminal
  // for an existing register. Smartpay/SmartConnect caches state against the
  // register_id, so re-pairing with the SAME register_id to a NEW physical
  // terminal can leave transactions routed to the OLD device. The fix:
  //   1) DELETE the old register record from our DB
  //   2) Generate a fresh register_id (with a timestamp suffix so it's unique)
  //   3) Clear the pairing_code field so staff can enter the new one
  //   4) Persist + tell staff "now enter the new pairing code and click Pair"
  const replaceTerminal = async (storeCode, terminal) => {
    const ok = window.confirm(
      `Replace physical terminal for "${terminal.name || 'this register'}"?\n\n` +
      'Use this when you have a NEW EFTPOS terminal device. It will:\n' +
      `  • Unregister the OLD device from SmartConnect (so old terminal stops being used)\n` +
      `  • Generate a fresh Register ID so the new terminal pairs cleanly\n` +
      `  • Clear the pairing code field so you can enter the one from the new terminal\n\n` +
      'Continue?'
    );
    if (!ok) return;
    setPairingSmartpay(terminal.id);
    try {
      // 1) Best-effort delete on backend (no-op if it doesn't exist)
      if (terminal.register_id) {
        try {
          await axios.delete(`${API_URL}/smartpay/registers/${encodeURIComponent(terminal.register_id)}`, { headers: getAuthHeader() });
        } catch (e) {
          console.warn('Old register delete failed (ignoring):', e?.message);
        }
      }
      // 2) Generate a fresh register_id and clear pairing/paired state
      const stamp = Date.now().toString(36).slice(-5);
      const baseName = (terminal.register_id || `${storeCode}-register`).replace(/-(v\d+|[a-z0-9]{5})$/i, '');
      const freshRegisterId = `${baseName}-${stamp}`;
      const updated = (storeTerminals[storeCode] || []).map(t => t.id === terminal.id
        ? { ...t, register_id: freshRegisterId, pairing_code: '', paired: false }
        : t);
      setStoreTerminals(prev => ({ ...prev, [storeCode]: updated }));
      await persistTerminals(storeCode, updated);
      toast.success(`Old terminal removed. New Register ID set: ${freshRegisterId}. Enter the new pairing code below and click "Pair".`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Replace terminal failed');
    } finally {
      setPairingSmartpay(null);
    }
  };

  // Save the full terminals[] array for a store (used after pair/remove)
  const persistTerminals = async (storeCode, terminalsArray) => {
    try {
      await axios.put(`${API_URL}/stores/${storeCode}`, { terminals: terminalsArray }, { headers: getAuthHeader() });
    } catch (err) {
      console.error('Failed to persist terminals', err);
      toast.error('Could not save terminal list');
    }
  };

  const pairOneTerminal = async (storeCode, terminal) => {
    if (!terminal.pairing_code) return toast.error('Enter the pairing code from this terminal');
    if (!terminal.register_id) return toast.error('Enter a Register ID');
    setPairingSmartpay(terminal.id);
    try {
      const response = await axios.post(`${API_URL}/smartpay/pair`, {
        pairing_code: terminal.pairing_code,
        register_id: terminal.register_id,
        register_name: terminal.name || 'Main Register',
        store: storeCode,
        environment: terminal.environment || 'prod',
      }, { headers: getAuthHeader() });
      if (response.data.success) {
        const updated = (storeTerminals[storeCode] || []).map(t => t.id === terminal.id ? { ...t, paired: true } : t);
        setStoreTerminals(prev => ({ ...prev, [storeCode]: updated }));
        await persistTerminals(storeCode, updated);
        toast.success(`${terminal.name || 'Terminal'} paired successfully`);
      } else {
        toast.error(`Pairing failed: ${response.data.error || response.data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Pairing request failed');
    } finally {
      setPairingSmartpay(null);
    }
  };
  
  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/hardware`, {
        scanner: scannerSettings,
        printers: printerSettings,
        label_printer: labelPrinterSettings,
        smartpay: smartpaySettings
      }, { headers: getAuthHeader() });
      
      toast.success('Hardware settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const updateSmartpaySetting = (store, field, value) => {
    setSmartpaySettings({
      ...smartpaySettings,
      [store]: {
        ...smartpaySettings[store],
        [field]: value
      }
    });
  };
  
  const testSmartpayConnection = async (store) => {
    const settings = smartpaySettings[store];
    if (!settings.ip_address) {
      toast.error('Please enter Smartpay IP address');
      return;
    }
    
    setTestingSmartpay(store);
    try {
      const response = await axios.post(`${API_URL}/smartpay/test`, {
        ip_address: settings.ip_address,
        port: settings.port || '80'
      }, { headers: getAuthHeader() });
      
      if (response.data.connected) {
        setSmartpayStatus(prev => ({
          ...prev,
          [store]: { ...prev[store], connected: true, status: 'connected' }
        }));
        toast.success(`Smartpay ${store} connected!`);
      } else {
        setSmartpayStatus(prev => ({
          ...prev,
          [store]: { ...prev[store], connected: false, status: response.data.error || 'failed' }
        }));
        toast.error(`Smartpay connection failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      setSmartpayStatus(prev => ({
        ...prev,
        [store]: { ...prev[store], connected: false, status: 'error' }
      }));
      toast.error('Failed to test Smartpay connection');
    } finally {
      setTestingSmartpay(null);
    }
  };
  
  const pairSmartpayTerminal = async (store) => {
    const settings = smartpaySettings[store];
    if (!settings.pairing_code) {
      toast.error('Enter the pairing code from the terminal first');
      return;
    }
    if (!settings.register_id) {
      toast.error('Enter a Register ID first');
      return;
    }

    setPairingSmartpay(store);
    try {
      const response = await axios.post(`${API_URL}/smartpay/pair`, {
        pairing_code: settings.pairing_code,
        register_id: settings.register_id,
        register_name: settings.register_name || 'Main Register',
        store: store,
        environment: settings.environment || 'prod',
      }, { headers: getAuthHeader() });

      if (response.data.success) {
        setSmartpayStatus(prev => ({
          ...prev,
          [store]: { ...prev[store], paired: true, connected: true }
        }));
        
        // Save the pairing info
        updateSmartpaySetting(store, 'terminal_id', response.data.terminal_id);
        updateSmartpaySetting(store, 'paired', true);
        updateSmartpaySetting(store, 'pairing_code', ''); // Clear the code after success
        
        toast.success(`Terminal paired successfully for ${store}!`);
      } else {
        toast.error(`Pairing failed: ${response.data.error || 'Invalid pairing code'}`);
      }
    } catch (error) {
      toast.error('Failed to pair terminal. Check the pairing code and try again.');
    } finally {
      setPairingSmartpay(null);
    }
  };
  
  const updatePrinterSetting = (store, field, value) => {
    setPrinterSettings({
      ...printerSettings,
      [store]: {
        ...printerSettings[store],
        [field]: value
      }
    });
  };
  
  const testPrinterConnection = async (store) => {
    const printer = printerSettings[store];
    if (!printer.ip_address) {
      toast.error('Please enter printer IP address first');
      return;
    }
    
    setTestingPrinter(true);
    setPrinterStatus({
      ...printerStatus,
      [store]: { connected: false, status: 'testing' }
    });
    
    try {
      const response = await axios.post(`${API_URL}/hardware/test-printer`, {
        ip_address: printer.ip_address,
        port: printer.port || '80',
        type: printer.type
      }, { headers: getAuthHeader() });
      
      if (response.data.connected) {
        setPrinterStatus({
          ...printerStatus,
          [store]: { connected: true, status: 'online' }
        });
        toast.success(`Printer at ${store.charAt(0).toUpperCase() + store.slice(1)} is connected`);
      } else {
        setPrinterStatus({
          ...printerStatus,
          [store]: { connected: false, status: 'offline' }
        });
        toast.warning('Printer not responding');
      }
    } catch (error) {
      setPrinterStatus({
        ...printerStatus,
        [store]: { connected: false, status: 'error' }
      });
      toast.error('Could not connect to printer');
    } finally {
      setTestingPrinter(false);
    }
  };
  
  const printTestReceipt = async (store) => {
    const printer = printerSettings[store];
    if (!printer.ip_address) {
      toast.error('Please configure printer IP first');
      return;
    }
    
    setTestingPrinter(true);
    try {
      await axios.post(`${API_URL}/hardware/print-test`, {
        ip_address: printer.ip_address,
        port: printer.port || '80',
        type: printer.type,
        store_name: store.charAt(0).toUpperCase() + store.slice(1)
      }, { headers: getAuthHeader() });
      
      toast.success('Test receipt sent to printer');
    } catch (error) {
      toast.error('Failed to print test receipt');
    } finally {
      setTestingPrinter(false);
    }
  };
  
  const testLabelPrinter = async () => {
    if (currentLabelSettings.connection === 'network' && !currentLabelSettings.ip_address) {
      toast.error('Please enter label printer IP address');
      return;
    }

    setLabelPrinterStatus({ connected: false, status: 'testing' });

    try {
      const response = await axios.post(`${API_URL}/hardware/test-label-printer`, {
        ...currentLabelSettings,
        store: selectedLabelStore,
      }, { headers: getAuthHeader() });

      if (response.data.connected) {
        setLabelPrinterStatus({ connected: true, status: 'online' });
        toast.success(`Zebra label printer (${storeName(selectedLabelStore)}) connected!`);
      } else {
        setLabelPrinterStatus({ connected: false, status: 'offline' });
        toast.warning('Label printer not responding');
      }
    } catch (error) {
      setLabelPrinterStatus({ connected: false, status: 'error' });
      toast.error('Could not connect to label printer');
    }
  };
  
  const printTestLabel = () => {
    printLabels([{
      name: 'Test Product Label',
      sell_price: 29.99,
      barcode: '9421903111111'
    }], 1);
  };

  // Browser-based label printing for Zebra GC420d
  // Labels: 30mm wide x 22mm tall, 3 across per row
  // Each product prints exactly 3 labels (one row)
  const printLabels = (products, copiesEach = 1) => {
    const JsBarcode = require('jsbarcode');
    
    // Auto-detect barcode format
    const detectFormat = (val) => {
      if (!val) return 'CODE128';
      const c = val.trim();
      if (/^\d{13}$/.test(c)) return 'EAN13';
      if (/^\d{12}$/.test(c)) return 'UPC';
      return 'CODE128';
    };

    const generateBarcodeSVG = (value) => {
      if (!value) return '';
      const canvas = document.createElement('canvas');
      const fmt = detectFormat(value);
      try {
        JsBarcode(canvas, value.trim(), { format: fmt, width: 1.5, height: 30, displayValue: false, margin: 0 });
        return canvas.toDataURL('image/png');
      } catch {
        try {
          JsBarcode(canvas, value.trim(), { format: 'CODE128', width: 1.5, height: 30, displayValue: false, margin: 0 });
          return canvas.toDataURL('image/png');
        } catch { return ''; }
      }
    };

    const labels = [];
    products.forEach(p => {
      const barcodeImg = generateBarcodeSVG(p.barcode || p.sku);
      labels.push({ ...p, barcodeImg });
    });

    // Page: 30mm wide x 22mm tall (single label)
    const labelHTML = `
      <html><head><title>Labels</title>
      <style>
        @page { margin: 0; size: 30mm 22mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; }
        .label {
          width: 30mm;
          height: 22mm;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0.3mm 0.5mm;
          overflow: hidden;
        }
        .store { font-size: 4pt; font-weight: normal; text-transform: uppercase; line-height: 1; letter-spacing: 0.2px; }
        .name { font-size: 7pt; font-weight: bold; line-height: 1.05; max-height: 6mm; overflow: hidden; word-break: break-word; margin: 0.2mm 0; }
        .barcode-img { width: 26mm; height: 8mm; object-fit: contain; margin: 0.3mm 0; }
        .barcode-num { font-size: 6pt; font-family: monospace; line-height: 1; }
        .price { font-size: 9pt; font-weight: bold; line-height: 1; }
      </style>
      </head><body>
      ${labels.map(p => `
        <div class="label">
          <div class="store">${localStorage.getItem('business_name') || 'SupplyTrack'}</div>
          <div class="name">${p.name}</div>
          ${p.barcodeImg ? `<img class="barcode-img" src="${p.barcodeImg}" />` : ''}
          ${p.barcode ? `<div class="barcode-num">${p.barcode}</div>` : ''}
          <div class="price">$${parseFloat(p.sell_price || p.price || 0).toFixed(2)}</div>
        </div>
      `).join('')}
      </body></html>
      </body></html>
    `;

    // Try QZ Tray first for direct Zebra printing
    import('../services/PrinterService').then(({ printLabels: qzPrintLabels }) => {
      qzPrintLabels(labelHTML);
    }).catch(() => {
      // Fallback to browser print
      let printFrame = document.getElementById('label-print-frame');
      if (!printFrame) {
        printFrame = document.createElement('iframe');
        printFrame.id = 'label-print-frame';
        printFrame.style.display = 'none';
        document.body.appendChild(printFrame);
      }
      printFrame.contentDocument.open();
      printFrame.contentDocument.write(labelHTML);
      printFrame.contentDocument.close();
      setTimeout(() => printFrame.contentWindow.print(), 500);
    });
    
    toast.success(`Printing ${labels.length} labels`);
  };
  
  // Expose printLabels globally so other pages can use it
  window.printLabels = printLabels;

  // Generate ZPL preview
  const generateZPLPreview = () => {
    const zpl = `^XA
^FO50,30^A0N,30,30^FDTest Product Label^FS
^FO50,70^BY2^BCN,80,Y,N,N^FD1234567890123^FS
^FO50,170^A0N,40,40^FD$29.99^FS
^XZ`;
    return zpl;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Hardware Settings
          </h1>
          <p className="text-slate-500 mt-1">Configure barcode scanners and receipt printers</p>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Settings
        </Button>
      </div>
      
      <Tabs defaultValue="store" className="space-y-6">
        <TabsList>
          <TabsTrigger value="store" className="flex items-center gap-2">
            <Settings className="w-4 h-4" /> Store Details
          </TabsTrigger>
          <TabsTrigger value="smartpay" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Smartpay EFTPOS
          </TabsTrigger>
          <TabsTrigger value="scanner" className="flex items-center gap-2">
            <Scan className="w-4 h-4" /> Barcode Scanner
          </TabsTrigger>
          <TabsTrigger value="printer" className="flex items-center gap-2">
            <Printer className="w-4 h-4" /> Receipt Printer
          </TabsTrigger>
          <TabsTrigger value="label" className="flex items-center gap-2">
            <Printer className="w-4 h-4" /> Label Printer
          </TabsTrigger>
          <TabsTrigger value="qztray" className="flex items-center gap-2">
            <Monitor className="w-4 h-4" /> Auto-Print
          </TabsTrigger>
        </TabsList>
        
        {/* Store Details */}
        <TabsContent value="store">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stores.map((store) => (
              <Card key={store.code}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-emerald-500" /> {store.name} Store
                  </CardTitle>
                  <CardDescription>
                    Store details shown on receipts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      placeholder="123 Main Street, Auckland"
                      value={storeDetails[store.code]?.address || ''}
                      onChange={(e) => updateStoreDetail(store.code, 'address', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      placeholder="09 123 4567"
                      value={storeDetails[store.code]?.phone || ''}
                      onChange={(e) => updateStoreDetail(store.code, 'phone', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      placeholder="www.thewellnessstore.co.nz"
                      value={storeDetails[store.code]?.website || ''}
                      onChange={(e) => updateStoreDetail(store.code, 'website', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>GST Number</Label>
                    <Input
                      placeholder="123 456 789"
                      value={storeDetails[store.code]?.gst_number || ''}
                      onChange={(e) => updateStoreDetail(store.code, 'gst_number', e.target.value)}
                    />
                  </div>
                  <div className="pt-4">
                    <Button 
                      onClick={() => saveStoreDetails(store.code)}
                      disabled={savingStore === store.code}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {savingStore === store.code ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save Store Details</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        {/* Smartpay EFTPOS Settings — multi-terminal per store */}
        <TabsContent value="smartpay">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {['orewa', 'eastridge'].map((store) => {
              const terminals = storeTerminals[store] || [];
              const pairedCount = terminals.filter(t => t.paired).length;
              return (
                <Card key={store}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 capitalize">
                        <CreditCard className="w-5 h-5 text-emerald-500" /> {store} EFTPOS
                      </CardTitle>
                      <Badge variant="secondary" className={pairedCount > 0 ? 'bg-emerald-100 text-emerald-700' : ''}>
                        {pairedCount} / {terminals.length} paired
                      </Badge>
                    </div>
                    <CardDescription>
                      One card per terminal. Add as many as you need for this store.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {terminals.length === 0 && (
                      <div className="text-center py-6 text-sm text-slate-400 border-2 border-dashed rounded-md">
                        No terminals configured yet for {store}
                      </div>
                    )}
                    {terminals.map((t) => (
                      <div key={t.id} className="border-2 rounded-md p-3 space-y-3 bg-slate-50">
                        <div className="flex items-center justify-between gap-2">
                          <Input
                            value={t.name}
                            onChange={(e) => updateTerminal(store, t.id, { name: e.target.value })}
                            placeholder="Terminal name (e.g. Front Counter)"
                            className="font-semibold bg-white"
                            data-testid={`sp-terminal-name-${t.id}`}
                          />
                          {t.paired ? (
                            <Badge className="bg-emerald-500 shrink-0"><Check className="w-3 h-3 mr-1" /> Paired</Badge>
                          ) : (
                            <Badge variant="secondary" className="shrink-0">Not paired</Badge>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 shrink-0" onClick={() => removeTerminal(store, t.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[11px] uppercase text-slate-500">Environment</Label>
                            <select
                              value={t.environment}
                              onChange={(e) => updateTerminal(store, t.id, { environment: e.target.value })}
                              className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm h-9 bg-white"
                            >
                              <option value="prod">Production</option>
                              <option value="dev">Dev / Sandbox</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-[11px] uppercase text-slate-500">Register ID</Label>
                            <Input
                              value={t.register_id}
                              onChange={(e) => updateTerminal(store, t.id, { register_id: e.target.value })}
                              placeholder={`${store}-register-1`}
                              className="h-9 text-sm bg-white"
                              data-testid={`sp-register-id-${t.id}`}
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-[11px] uppercase text-slate-500">Pairing Code from Terminal</Label>
                          <Input
                            value={t.pairing_code}
                            onChange={(e) => updateTerminal(store, t.id, { pairing_code: e.target.value.toUpperCase().trim() })}
                            placeholder="Code shown on the terminal"
                            className="text-center font-mono tracking-widest bg-white"
                            maxLength={12}
                            data-testid={`sp-pairing-code-${t.id}`}
                          />
                        </div>

                        <Button
                          onClick={() => pairOneTerminal(store, t)}
                          disabled={pairingSmartpay === t.id || !t.pairing_code || !t.register_id}
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`sp-pair-btn-${t.id}`}
                        >
                          {pairingSmartpay === t.id ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Pairing…</>
                          ) : t.paired ? (
                            <><CreditCard className="w-4 h-4 mr-2" /> Re-pair with new code</>
                          ) : (
                            <><CreditCard className="w-4 h-4 mr-2" /> Pair Terminal</>
                          )}
                        </Button>

                        {/* NEW: Replace physical terminal — for when you receive a brand-new EFTPOS device */}
                        {t.paired && (
                          <Button
                            variant="outline"
                            onClick={() => replaceTerminal(store, t)}
                            disabled={pairingSmartpay === t.id}
                            className="w-full mt-2 border-amber-300 text-amber-800 hover:bg-amber-50"
                            data-testid={`sp-replace-terminal-${t.id}`}
                            title="Use this when you have a NEW physical EFTPOS terminal replacing this one"
                          >
                            🔄 Got a NEW physical terminal? Replace it
                          </Button>
                        )}
                      </div>
                    ))}

                    <Button
                      variant="outline"
                      onClick={() => addTerminal(store)}
                      className="w-full border-dashed border-2"
                      data-testid={`sp-add-terminal-${store}`}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Another Terminal
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Smartpay Setup Guide</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-blue-50 rounded-sm">
                <h4 className="font-medium text-blue-800 mb-2">How to pair a Smartpay terminal (SmartConnect Cloud)</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Make sure the terminal has Wi-Fi/data and is online</li>
                  <li>On the terminal: <b>Menu → Integration → Pair POS</b> — note the pairing code</li>
                  <li>In the panel above, click <b>Add Another Terminal</b></li>
                  <li>Give it a name (e.g. "Front Counter"), pick Environment, set a unique Register ID</li>
                  <li>Type the pairing code, click <b>Pair Terminal</b></li>
                  <li>Repeat for every additional terminal in this store</li>
                </ol>
                <p className="mt-3 text-sm text-blue-600">
                  <strong>Pairing codes expire after a few minutes</strong> — if pairing fails, generate a fresh code on the terminal. The Register ID stays the same on re-pairing.
                </p>
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-sm font-bold text-amber-900">⚠️ Got a NEW physical terminal? (e.g. Smartpay sent a replacement)</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Just typing a new pairing code into the existing register can leave sales routed to the OLD device because SmartConnect Cloud caches state against the Register ID.
                  </p>
                  <p className="text-sm text-amber-800 mt-1">
                    👉 Use the orange <b>"🔄 Got a NEW physical terminal? Replace it"</b> button on the terminal card. It deletes the old registration, generates a fresh Register ID, and clears the pairing code field. Then enter the new pairing code &amp; click <b>Pair Terminal</b>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scanner Settings */}
        <TabsContent value="scanner">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scan className="w-5 h-5 text-blue-500" /> Symbol Wireless Scanner
              </CardTitle>
              <CardDescription>
                Configure your Symbol wireless barcode scanner. The scanner works in keyboard wedge mode - 
                it sends barcode data as keystrokes to the focused input field.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-sm">
                <h4 className="font-medium text-blue-800 mb-2">How it works</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Your Symbol scanner sends barcode data as keyboard input</li>
                  <li>• When you scan, it types the barcode into the search field</li>
                  <li>• The scanner should be configured to send Enter key after each scan</li>
                  <li>• Product is automatically added to cart when barcode matches</li>
                </ul>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      Scanner Enabled
                    </Label>
                    <Switch
                      checked={scannerSettings.enabled}
                      onCheckedChange={(checked) => setScannerSettings({...scannerSettings, enabled: checked})}
                      data-testid="scanner-enabled"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      Beep on Successful Scan
                    </Label>
                    <Switch
                      checked={scannerSettings.beep_on_scan}
                      onCheckedChange={(checked) => setScannerSettings({...scannerSettings, beep_on_scan: checked})}
                      data-testid="scanner-beep"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      Auto-add to Cart
                    </Label>
                    <Switch
                      checked={scannerSettings.auto_add_to_cart}
                      onCheckedChange={(checked) => setScannerSettings({...scannerSettings, auto_add_to_cart: checked})}
                      data-testid="scanner-auto-add"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      Focus Search on Scan
                    </Label>
                    <Switch
                      checked={scannerSettings.focus_search_on_scan}
                      onCheckedChange={(checked) => setScannerSettings({...scannerSettings, focus_search_on_scan: checked})}
                      data-testid="scanner-focus"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Suffix Key (sent after barcode)</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-sm"
                      value={scannerSettings.suffix_key}
                      onChange={(e) => setScannerSettings({...scannerSettings, suffix_key: e.target.value})}
                      data-testid="scanner-suffix"
                    >
                      <option value="Enter">Enter (Recommended)</option>
                      <option value="Tab">Tab</option>
                      <option value="None">None</option>
                    </select>
                    <p className="text-xs text-slate-500">Configure your scanner to send this key after each barcode</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-sm">
                <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Scanner Setup Instructions
                </h4>
                <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Ensure your Symbol scanner is paired via Bluetooth or USB dongle</li>
                  <li>Configure scanner to "Keyboard Wedge" or "HID" mode</li>
                  <li>Set suffix character to Enter (scan the "Add Enter Suffix" barcode in manual)</li>
                  <li>Test by clicking in the POS search box and scanning a product</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Printer Settings */}
        <TabsContent value="printer">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {['orewa', 'eastridge'].map((store) => (
              <Card key={store}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Printer className={`w-5 h-5 ${store === 'orewa' ? 'text-blue-500' : 'text-emerald-500'}`} />
                      {store.charAt(0).toUpperCase() + store.slice(1)} Printer
                    </span>
                    {printerStatus[store].status === 'online' ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <Wifi className="w-3 h-3 mr-1" /> Connected
                      </Badge>
                    ) : printerStatus[store].status === 'testing' ? (
                      <Badge className="bg-blue-100 text-blue-700">
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing
                      </Badge>
                    ) : printerStatus[store].status === 'offline' ? (
                      <Badge className="bg-red-100 text-red-700">
                        <WifiOff className="w-3 h-3 mr-1" /> Offline
                      </Badge>
                    ) : null}
                  </CardTitle>
                  <CardDescription>Star TSP100 FuturePRNT Configuration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Printer Enabled</Label>
                    <Switch
                      checked={printerSettings[store].enabled}
                      onCheckedChange={(checked) => updatePrinterSetting(store, 'enabled', checked)}
                      data-testid={`${store}-printer-enabled`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Printer Type</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-sm"
                      value={printerSettings[store].type}
                      onChange={(e) => updatePrinterSetting(store, 'type', e.target.value)}
                      data-testid={`${store}-printer-type`}
                    >
                      <option value="star_tsp100">Star TSP100 FuturePRNT</option>
                      <option value="star_tsp650">Star TSP650II</option>
                      <option value="epson_tm">Epson TM Series</option>
                      <option value="browser">Browser Print (Fallback)</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>IP Address</Label>
                      <Input
                        placeholder="192.168.1.100"
                        value={printerSettings[store].ip_address}
                        onChange={(e) => updatePrinterSetting(store, 'ip_address', e.target.value)}
                        data-testid={`${store}-printer-ip`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Port</Label>
                      <Input
                        placeholder="80"
                        value={printerSettings[store].port}
                        onChange={(e) => updatePrinterSetting(store, 'port', e.target.value)}
                        data-testid={`${store}-printer-port`}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Paper Width</Label>
                      <select
                        className="w-full h-10 px-3 border rounded-sm"
                        value={printerSettings[store].paper_width}
                        onChange={(e) => updatePrinterSetting(store, 'paper_width', e.target.value)}
                        data-testid={`${store}-paper-width`}
                      >
                        <option value="80mm">80mm (Standard)</option>
                        <option value="58mm">58mm (Narrow)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Print Copies</Label>
                      <Input
                        type="number"
                        min="1"
                        max="3"
                        value={printerSettings[store].print_copies}
                        onChange={(e) => updatePrinterSetting(store, 'print_copies', parseInt(e.target.value) || 1)}
                        data-testid={`${store}-print-copies`}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label>Auto-print Receipt After Sale</Label>
                    <Switch
                      checked={printerSettings[store].auto_print_receipt}
                      onCheckedChange={(checked) => updatePrinterSetting(store, 'auto_print_receipt', checked)}
                      data-testid={`${store}-auto-print`}
                    />
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => testPrinterConnection(store)}
                      disabled={testingPrinter}
                      className="flex-1"
                    >
                      {testingPrinter ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Test Connection
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => printTestReceipt(store)}
                      disabled={testingPrinter || !printerSettings[store].ip_address}
                      className="flex-1"
                    >
                      <TestTube className="w-4 h-4 mr-2" />
                      Print Test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> Setup Instructions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Star TSP100 Network Setup</h4>
                  <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Connect printer to your network via Ethernet or WiFi</li>
                    <li>Print a self-test to get the IP address (hold Feed while powering on)</li>
                    <li>Enter the IP address above</li>
                    <li>Ensure printer and POS are on the same network</li>
                    <li>Click "Test Connection" to verify</li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Star CloudPRNT (Alternative)</h4>
                  <p className="text-sm text-slate-600 mb-2">
                    If direct connection doesn't work, you can use Star CloudPRNT service:
                  </p>
                  <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Register at starmicronics.com/cloudprnt</li>
                    <li>Add your printer to the cloud service</li>
                    <li>Use the cloud URL instead of IP address</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Label Printer Settings - Zebra GC420 */}
        <TabsContent value="label">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Printer className={`w-5 h-5 ${selectedLabelStore === 'orewa' ? 'text-blue-500' : 'text-emerald-500'}`} />
                    Zebra GC420 Label Printer — {storeName(selectedLabelStore)}
                  </span>
                  {labelPrinterStatus.status === 'online' ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <Wifi className="w-3 h-3 mr-1" /> Connected
                    </Badge>
                  ) : labelPrinterStatus.status === 'testing' ? (
                    <Badge className="bg-blue-100 text-blue-700">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing
                    </Badge>
                  ) : labelPrinterStatus.status === 'offline' ? (
                    <Badge className="bg-red-100 text-red-700">
                      <WifiOff className="w-3 h-3 mr-1" /> Offline
                    </Badge>
                  ) : null}
                </CardTitle>
                <CardDescription>Configure your Zebra GC420 for product label printing — each store has its own settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Store toggle */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-md" data-testid="label-store-toggle">
                  <button
                    onClick={() => { setSelectedLabelStore('orewa'); setLabelPrinterStatus({ connected: false, status: 'unknown' }); }}
                    className={`flex-1 py-2 px-3 rounded text-sm font-semibold transition-colors ${
                      selectedLabelStore === 'orewa'
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="label-store-orewa"
                  >
                    {storeName('orewa')} {labelPrinterSettings.orewa?.enabled && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-emerald-500" title="Enabled" />}
                  </button>
                  <button
                    onClick={() => { setSelectedLabelStore('eastridge'); setLabelPrinterStatus({ connected: false, status: 'unknown' }); }}
                    className={`flex-1 py-2 px-3 rounded text-sm font-semibold transition-colors ${
                      selectedLabelStore === 'eastridge'
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="label-store-eastridge"
                  >
                    {storeName('eastridge')} {labelPrinterSettings.eastridge?.enabled && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-emerald-500" title="Enabled" />}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <Label>Label Printer Enabled</Label>
                  <Switch
                    checked={currentLabelSettings.enabled}
                    onCheckedChange={(checked) => updateCurrentLabelSettings({ enabled: checked })}
                    data-testid="label-printer-enabled"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Connection Type</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-sm"
                    value={currentLabelSettings.connection}
                    onChange={(e) => updateCurrentLabelSettings({ connection: e.target.value })}
                  >
                    <option value="usb">USB (Direct)</option>
                    <option value="network">Network (Ethernet/WiFi)</option>
                    <option value="browser_print">Zebra Browser Print</option>
                  </select>
                </div>

                {currentLabelSettings.connection === 'network' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>IP Address</Label>
                      <Input
                        placeholder="192.168.1.150"
                        value={currentLabelSettings.ip_address}
                        onChange={(e) => updateCurrentLabelSettings({ ip_address: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Port</Label>
                      <Input
                        placeholder="9100"
                        value={currentLabelSettings.port}
                        onChange={(e) => updateCurrentLabelSettings({ port: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Label Width (mm)</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-sm"
                      value={currentLabelSettings.label_width}
                      onChange={(e) => updateCurrentLabelSettings({ label_width: e.target.value })}
                    >
                      <option value="30">30mm (3-across)</option>
                      <option value="40">40mm</option>
                      <option value="50">50mm (Standard)</option>
                      <option value="60">60mm</option>
                      <option value="100">100mm (Wide)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Label Height (mm)</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-sm"
                      value={currentLabelSettings.label_height}
                      onChange={(e) => updateCurrentLabelSettings({ label_height: e.target.value })}
                    >
                      <option value="22">22mm</option>
                      <option value="25">25mm (Standard)</option>
                      <option value="30">30mm</option>
                      <option value="40">40mm</option>
                      <option value="50">50mm</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Print Density</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-sm"
                      value={currentLabelSettings.print_density}
                      onChange={(e) => updateCurrentLabelSettings({ print_density: e.target.value })}
                    >
                      <option value="5">5 (Light)</option>
                      <option value="8">8 (Normal)</option>
                      <option value="10">10 (Dark)</option>
                      <option value="15">15 (Very Dark)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Print Speed</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-sm"
                      value={currentLabelSettings.print_speed}
                      onChange={(e) => updateCurrentLabelSettings({ print_speed: e.target.value })}
                    >
                      <option value="1">1 (Slow - Best Quality)</option>
                      <option value="2">2 (Normal)</option>
                      <option value="3">3 (Fast)</option>
                      <option value="4">4 (Very Fast)</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Barcode Type</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-sm"
                    value={currentLabelSettings.barcode_type}
                    onChange={(e) => updateCurrentLabelSettings({ barcode_type: e.target.value })}
                  >
                    <option value="code128">Code 128 (Recommended)</option>
                    <option value="ean13">EAN-13</option>
                    <option value="upc">UPC-A</option>
                    <option value="code39">Code 39</option>
                  </select>
                </div>

                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-medium">Label Contents</Label>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Include Barcode</span>
                    <Switch
                      checked={currentLabelSettings.include_barcode}
                      onCheckedChange={(checked) => updateCurrentLabelSettings({ include_barcode: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Include Price</span>
                    <Switch
                      checked={currentLabelSettings.include_price}
                      onCheckedChange={(checked) => updateCurrentLabelSettings({ include_price: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Include Store Name</span>
                    <Switch
                      checked={currentLabelSettings.include_store_name}
                      onCheckedChange={(checked) => updateCurrentLabelSettings({ include_store_name: checked })}
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={testLabelPrinter}
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Test Connection
                  </Button>
                  <Button
                    variant="outline"
                    onClick={printTestLabel}
                    className="flex-1"
                  >
                    <TestTube className="w-4 h-4 mr-2" />
                    Print Test Label
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* ZPL Preview & Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-slate-500" />
                  Setup & Label Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-2">Zebra GC420 Features</h4>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• Direct thermal printing (no ribbon needed)</li>
                    <li>• 203 DPI resolution</li>
                    <li>• USB, Serial, and optional Ethernet</li>
                    <li>• ZPL II programming language</li>
                    <li>• Max print width: 104mm</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-slate-100 rounded-lg">
                  <h4 className="font-medium mb-2">Label Preview (ZPL)</h4>
                  <pre className="text-xs font-mono bg-white p-3 rounded border overflow-x-auto">
{generateZPLPreview()}
                  </pre>
                </div>
                
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2">Setup Instructions</h4>
                  <ol className="text-sm text-amber-700 space-y-2 list-decimal list-inside">
                    <li><strong>USB Connection:</strong> Install Zebra driver from zebra.com/drivers</li>
                    <li><strong>Network Setup:</strong> Configure IP via printer's LCD or Zebra Setup Utilities</li>
                    <li><strong>Browser Print:</strong> Install Zebra Browser Print from zebra.com</li>
                    <li>Load 50mm x 25mm direct thermal labels</li>
                    <li>Calibrate media: Hold Feed button for 3 seconds</li>
                    <li>Test print from Smart PO → Print Labels</li>
                  </ol>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Recommended Labels</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Size:</strong> 50mm x 25mm (2" x 1")</li>
                    <li>• <strong>Type:</strong> Direct Thermal (no ribbon)</li>
                    <li>• <strong>Core:</strong> 25mm (1") inner diameter</li>
                    <li>• <strong>Supplier:</strong> Zebra, DYMO, or generic thermal labels</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* QZ Tray Auto-Print Setup */}
        <TabsContent value="qztray">
          <div className="space-y-6">
            <Card className="border rounded-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                  <Monitor className="w-5 h-5" /> Auto-Print Setup (QZ Tray)
                </CardTitle>
                <CardDescription>
                  Assign each printer to a job type. Receipts, labels, and documents will print automatically — no choosing required.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Default print mode */}
                <div className="p-4 rounded-lg border-2 border-emerald-300 bg-emerald-50" data-testid="print-mode-card">
                  <p className="font-bold text-emerald-900 flex items-center gap-2 mb-2">
                    <Printer className="w-4 h-4" />
                    Print mode (default: Browser dialog — most reliable)
                  </p>
                  <p className="text-sm text-emerald-800 mb-3">
                    Every print job (receipts &amp; labels) opens the browser's native print dialog so staff pick the printer manually. Chrome remembers your choice. <b>This is the default because QZ Tray has been unreliable.</b>
                  </p>
                  <div className="flex items-start justify-between gap-4 pt-3 border-t border-emerald-200">
                    <div className="flex-1">
                      <p className="font-bold text-slate-900">Try QZ Tray first (only if it's actually working)</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Turn ON only if your QZ Tray is rock-solid and you want raw ZPL / ESC-POS printing. If QZ ever drops, the till still falls back to browser print so labels never block.
                      </p>
                    </div>
                    <Switch
                      checked={getQzPreferred()}
                      onCheckedChange={(v) => { setQzPreferred(v); toast.success(v ? 'QZ Tray will be tried first' : 'Browser print is the default (recommended)'); }}
                      data-testid="toggle-qz-preferred"
                    />
                  </div>
                  <div className="flex items-start justify-between gap-4 pt-3 mt-3 border-t border-emerald-200">
                    <div className="flex-1">
                      <p className="font-bold text-slate-900">Force browser print (skip QZ entirely)</p>
                      <p className="text-xs text-slate-600 mt-1">
                        If QZ Tray is installed but constantly disconnecting (e.g. at Eastridge), turn this ON to skip QZ completely.
                      </p>
                    </div>
                    <Switch
                      checked={getBrowserPrintMode()}
                      onCheckedChange={(v) => { setBrowserPrintMode(v); toast.success(v ? 'Browser print FORCED — QZ skipped' : 'Browser print no longer forced'); }}
                      data-testid="toggle-browser-print-mode"
                    />
                  </div>
                  <p className="text-xs text-emerald-700 mt-3 pt-3 border-t border-emerald-200">
                    💡 First-time tip: when the print dialog opens, click <b>"More settings → Paper size"</b> and choose <b>80mm</b> for receipts or <b>90×22mm</b> for labels. Chrome remembers it per printer.
                  </p>
                </div>

                {/* Connection Status */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${qzConnected ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                    <div>
                      <p className="font-medium">QZ Tray</p>
                      <p className="text-sm text-slate-500">{qzConnected ? 'Connected' : 'Not connected'}</p>
                    </div>
                  </div>
                  <Button 
                    onClick={detectPrinters} 
                    disabled={detectingPrinters}
                    variant="outline"
                    data-testid="detect-printers-btn"
                  >
                    {detectingPrinters ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Detecting...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" /> Detect Printers</>
                    )}
                  </Button>
                </div>

                {availablePrinters.length > 0 && (
                  <>
                    {/* Found Printers */}
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <p className="text-sm font-medium text-emerald-700 mb-2">Found {availablePrinters.length} printer(s):</p>
                      <div className="flex flex-wrap gap-2">
                        {availablePrinters.map(p => (
                          <Badge key={p} variant="secondary" className="text-sm">{p}</Badge>
                        ))}
                      </div>
                    </div>

                    {/* Printer Assignments */}
                    <div className="space-y-4">
                      {[
                        { key: 'receipt', label: 'Receipt Printer', desc: 'Prints after every sale (Star TSP143IIU)', icon: '🧾' },
                        { key: 'label', label: 'Label Printer', desc: 'Prints barcode labels (Zebra GC420d)', icon: '🏷' },
                        { key: 'document', label: 'A4 Document Printer', desc: 'Reports, purchase orders, etc.', icon: '📄' },
                      ].map(({ key, label, desc, icon }) => (
                        <div key={key} className="flex items-center gap-4 p-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`printer-assign-${key}`}>
                          <span className="text-2xl">{icon}</span>
                          <div className="flex-1">
                            <p className="font-semibold">{label}</p>
                            <p className="text-sm text-slate-500">{desc}</p>
                          </div>
                          <select
                            className="border rounded-lg px-3 py-2 text-sm min-w-[250px] bg-white"
                            value={printerAssignments[key] || ''}
                            onChange={(e) => savePrinterAssignment(key, e.target.value)}
                            data-testid={`printer-select-${key}`}
                          >
                            <option value="">-- Select Printer --</option>
                            {availablePrinters.map(p => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                          {printerAssignments[key] && (
                            <Badge className="bg-emerald-100 text-emerald-700"><Check className="w-3 h-3 mr-1" /> Set</Badge>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Status Summary */}
                    {printerAssignments.receipt && printerAssignments.label && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="font-medium text-blue-800">All set! Your printers are configured:</p>
                        <ul className="mt-2 space-y-1 text-sm text-blue-700">
                          <li>Receipts → <strong>{printerAssignments.receipt}</strong></li>
                          <li>Labels → <strong>{printerAssignments.label}</strong></li>
                          {printerAssignments.document && <li>Documents → <strong>{printerAssignments.document}</strong></li>}
                        </ul>
                      </div>
                    )}
                  </>
                )}

                {!qzConnected && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="font-medium text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> QZ Tray not detected
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      Make sure QZ Tray is installed and running. Look for the QZ icon in your system tray (bottom-right of your screen).
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HardwareSettings;
